import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * "Quem faz Gênese" — credits roll at the end. Film-style two-column
 * layout: role/registry on the left, names on the right. Each row
 * fades in as it enters the viewport.
 *
 * Hardcoded until the admin UI in Builder is built (Phase 3). When
 * it lands we'll lift this into `showcases.team` (JSON array of
 * `{ role, name, registry? }`) so the PM can edit without a deploy.
 */

interface Credit {
  role: string;
  name: string;
  registry?: string;
}

// Wave 314i — JP pediu engenheiro responsável antes do arquiteto.
const CREDITS: Credit[] = [
  { role: 'Engenheiro',          name: 'Anderson Reis',    registry: 'CREA 1333'  },
  { role: 'Arquiteto',          name: 'Igor Gonçalves',   registry: 'CREA 12222' },
  { role: 'Financeiro',          name: 'Renato Carneiro' },
];

export function Credits() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Whole section drifts gently upward — subtle film-credits feel
  // without the kitschy hard auto-scroll roll.
  const stageY = useTransform(scrollYProgress, [0, 1], ['6%', '-6%']);

  return (
    <section
      ref={ref}
      className="relative border-t border-line bg-bg py-28 sm:py-36"
      aria-label="Equipe do projeto"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(212,184,150,0.05), transparent 70%)',
        }}
      />

      <motion.div
        className="relative mx-auto max-w-4xl px-6 sm:px-12"
        style={{ y: stageY }}
      >
        <div className="text-center">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Quem faz Gênese
          </p>
          <h2 className="mt-5 font-display text-[clamp(2rem,5vw,3rem)] font-light leading-[1.05] tracking-tighter text-ink">
            Nomes, números, responsabilidades.
          </h2>
          <p className="mt-6 max-w-[52ch] mx-auto text-sm leading-relaxed text-ink-muted sm:text-base">
            Toda decisão construtiva tem um responsável técnico ligado à
            matrícula do imóvel. Transparência é condição, não bônus.
          </p>
        </div>

        <ol className="mt-16 divide-y divide-line">
          {CREDITS.map((c, i) => (
            <CreditRow key={c.role} credit={c} index={i} />
          ))}
        </ol>
      </motion.div>
    </section>
  );
}

function CreditRow({ credit, index }: { credit: Credit; index: number }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-1 gap-2 py-6 sm:grid-cols-[1fr_2fr] sm:items-baseline sm:gap-10"
    >
      <div className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink-subtle">
        {credit.role}
      </div>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <p className="font-display text-2xl font-light tracking-tight text-ink sm:text-3xl">
          {credit.name}
        </p>
        {credit.registry && (
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink-subtle tabular-nums">
            {credit.registry}
          </p>
        )}
      </div>
    </motion.li>
  );
}
